import React from 'react';
import { 
  AlertTriangle,
  Eye,
  FileQuestion,
  Clock,
  Brain,
  Frown
} from 'lucide-react';

const PAIN_POINTS = [
  {
    icon: FileQuestion,
    title: "Can't remember what you did",
    description: "Three months of work reduced to a blank page because you didn't keep notes."
  },
  {
    icon: Clock,
    title: "Running out of time",
    description: "Your review is due tomorrow and you've barely started writing."
  },
  {
    icon: Eye,
    title: "Underselling yourself",
    description: "You know you did good work, but struggle to articulate it convincingly."
  },
  {
    icon: Brain,
    title: "Overthinking every word",
    description: "Rewriting the same paragraph five times because it doesn't sound right."
  },
  {
    icon: Frown,
    title: "Dreading the process",
    description: "Performance reviews feel like a chore, not an opportunity."
  },
  {
    icon: AlertTriangle,
    title: "Missing your wins",
    description: "Important achievements slip through because you forgot to document them."
  }
];

const PainPoints = ({ series }) => {
  const accentColor = series?.accentColor || '#dc2626';

  return (
    <section className="py-20 bg-slate-50" data-testid="review-pain-points">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Sound familiar?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              You're not alone. Most professionals struggle with self-reviews because the process is broken.
            </p>
          </div>

          {/* Pain points grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PAIN_POINTS.map((point, index) => {
              const Icon = point.icon;
              return (
                <div 
                  key={index}
                  className="bg-white p-6 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors group"
                  data-testid={`pain-point-${index}`}
                >
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-slate-100 group-hover:bg-red-50 transition-colors"
                  >
                    <Icon className="w-6 h-6 text-slate-500 group-hover:text-red-500 transition-colors" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">
                    {point.title}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {point.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Transition statement */}
          <div className="mt-16 text-center">
            <p className="text-lg text-slate-700">
              <span className="font-semibold" style={{ color: accentColor }}>There's a better way.</span>
              {' '}Turn your work into words that actually land.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PainPoints;
